import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { z } from 'zod';

import { mutateUserInvite } from '@/api/user';
import type { UserRoleType } from '@/api/user/userType';
import { businessEmail, useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { maxEmailLength, maxUserNameLength } from '@/constants/numbers';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import PermissionItem from '@/features/PermissionGroup/PermissionItem';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import UserInviteHeader from './UserInviteHeader';

const UserInvite = () => {
  const navigate = useInstanceNavigate();
  const [isSaving, setSaving] = useState(false);
  const { pushNotification } = useNotification();
  const formSchema = z.object({
    name: z.string().min(1, t`Name is required`),
    email: businessEmail(),
    role: z.enum(['user', 'admin'] as const),
  });

  const form = useAppForm({
    defaultValues: {
      name: '',
      email: '',
      role: 'user' as UserRoleType,
    },
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        setSaving(true);

        try {
          const { success } = await mutateUserInvite(value);
          if (!success) {
            throw new Error();
          }

          navigate(ROUTES.users.path);
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`Unable to invite user, please try again or contact support.`,
          });
        } finally {
          setSaving(false);
        }
      },
    },
  });

  return (
    <Page title={t`Settings`} pageName={t`Invite User`} exampleAIQuestions={[]}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-6">
          <UserInviteHeader isSaving={isSaving} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-24">
            <div className="lg:col-span-24 xl:col-span-9 2xl:col-span-7">
              <Panel title={<Trans>User Details</Trans>} style={{ height: '100%' }}>
                <div className="flex w-full max-w-100 flex-col gap-6">
                  <form.AppField name="name">
                    {(field) => (
                      <field.Field>
                        <FieldLabel htmlFor="name">
                          <Trans>Name</Trans>
                        </FieldLabel>
                        <field.TextInput
                          id="name"
                          autoComplete="name"
                          spellCheck={false}
                          autoCapitalize="words"
                          autoFocus
                          maxLength={maxUserNameLength}
                          size="large"
                        />
                        <field.Error />
                      </field.Field>
                    )}
                  </form.AppField>
                  <form.AppField name="email">
                    {(field) => (
                      <field.Field>
                        <FieldLabel htmlFor="email">
                          <Trans>Email</Trans>
                        </FieldLabel>
                        <field.TextInput
                          id="email"
                          inputMode="email"
                          placeholder={t`name@work-email.com`}
                          autoComplete="email"
                          spellCheck={false}
                          autoCapitalize="none"
                          maxLength={maxEmailLength}
                          size="large"
                        />
                        <field.Error />
                      </field.Field>
                    )}
                  </form.AppField>
                </div>
              </Panel>
            </div>
            <div className="lg:col-span-24 xl:col-span-15 2xl:col-span-17">
              <Panel className="h-full" title={<Trans>Permission Setting</Trans>}>
                <form.AppField name="role">
                  {(field) => (
                    <field.Field>
                      <field.RadioGroup className="gap-6" disabled={isSaving}>
                        <PermissionItem
                          type="user"
                          isEditing={true}
                          disabled={isSaving}
                          role={field.state.value}
                          title={<Trans>User Permission</Trans>}
                          description={
                            <Trans>
                              Users have permissions to view all data, save filters and edit spend
                              information
                            </Trans>
                          }
                        />
                        <PermissionItem
                          type="admin"
                          isEditing={true}
                          disabled={isSaving}
                          role={field.state.value}
                          title={<Trans>Admin Permission</Trans>}
                          description={
                            <Trans>
                              Admins have all user permissions and the ability to map vendors, add
                              users, manage users and edit TAM
                            </Trans>
                          }
                        />
                      </field.RadioGroup>
                      <field.Error />
                    </field.Field>
                  )}
                </form.AppField>
              </Panel>
            </div>
          </div>
        </div>
      </form>
    </Page>
  );
};

export default UserInvite;
